/**
 * B2B 配送日カレンダーピッカー。select[data-pds-delivery-date] と同期し、既存の保存・検証ロジックを流用する。
 * @fileoverview b2b-delivery-date.js から init される。
 */

(function () {
  /**
   * @param {object} api
   * @returns {boolean}
   */
  function useCalendarPicker(api) {
    return api.config.datePickerMode !== 'select';
  }

  /**
   * @param {string} ymd
   * @param {object} api
   * @returns {string}
   */
  function formatYmdSlash(ymd, api) {
    if (!ymd) return '';
    const date = api.parseYmd(ymd);
    if (Number.isNaN(date.getTime())) return '';
    return `${date.getFullYear()} / ${date.getMonth() + 1} / ${date.getDate()}`;
  }

  /**
   * @param {Date} date
   * @param {Date} earliest
   * @param {Date} max
   * @param {Set<string>} holidays
   * @param {object} api
   * @returns {boolean}
   */
  function isDaySelectable(date, earliest, max, holidays, api) {
    const dayStart = new Date(date.getFullYear(), date.getMonth(), date.getDate());
    const earliestStart = new Date(earliest.getFullYear(), earliest.getMonth(), earliest.getDate());
    const maxStart = new Date(max.getFullYear(), max.getMonth(), max.getDate());
    if (dayStart < earliestStart || dayStart > maxStart) return false;
    return api.isDeliverable(date, holidays);
  }

  /**
   * @param {number} year
   * @param {number} month
   * @returns {Date}
   */
  function firstOfMonth(year, month) {
    return new Date(year, month, 1);
  }

  /**
   * @param {HTMLElement} calendarRoot
   * @param {object} state
   */
  function renderMonth(calendarRoot, state) {
    const { api, viewYear, viewMonth, selectedYmd, earliest, max, holidays } = state;
    const grid = calendarRoot.querySelector('[data-pds-calendar-grid]');
    const monthLabel = calendarRoot.querySelector('[data-pds-calendar-month-label]');
    const prevBtn = calendarRoot.querySelector('[data-pds-calendar-prev]');
    const nextBtn = calendarRoot.querySelector('[data-pds-calendar-next]');

    if (!grid || !monthLabel) return;

    monthLabel.textContent = `${viewYear}-${String(viewMonth + 1).padStart(2, '0')}`;

    const viewStart = firstOfMonth(viewYear, viewMonth);
    const earliestMonth = firstOfMonth(earliest.getFullYear(), earliest.getMonth());
    const maxMonth = firstOfMonth(max.getFullYear(), max.getMonth());

    if (prevBtn) prevBtn.disabled = viewStart <= earliestMonth;
    if (nextBtn) nextBtn.disabled = viewStart >= maxMonth;

    const startOffset = viewStart.getDay();
    const gridStart = new Date(viewYear, viewMonth, 1 - startOffset);

    grid.innerHTML = '';

    for (let i = 0; i < 42; i += 1) {
      const cellDate = api.addCalendarDays(gridStart, i);
      const ymd = api.formatYmd(cellDate);
      const inMonth = cellDate.getMonth() === viewMonth;
      const dow = cellDate.getDay();
      const isHoliday = holidays.has(ymd);
      const selectable = isDaySelectable(cellDate, earliest, max, holidays, api);
      const isSelected = selectedYmd === ymd;

      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'pds-delivery-calendar__day';
      btn.textContent = String(cellDate.getDate());
      btn.dataset.date = ymd;

      if (!inMonth) btn.classList.add('pds-delivery-calendar__day--outside');
      if (dow === 0) btn.classList.add('pds-delivery-calendar__day--sun');
      if (dow === 6) btn.classList.add('pds-delivery-calendar__day--sat');
      if (isHoliday) btn.classList.add('pds-delivery-calendar__day--holiday');

      if (isSelected) {
        btn.classList.add('pds-delivery-calendar__day--selected');
        btn.setAttribute('aria-pressed', 'true');
      }

      if (selectable) {
        btn.classList.add('pds-delivery-calendar__day--selectable');
      } else {
        btn.classList.add('pds-delivery-calendar__day--disabled');
        btn.disabled = true;
      }

      grid.appendChild(btn);
    }
  }

  /**
   * @param {HTMLElement} root
   * @param {object} config
   * @param {HTMLSelectElement} dateSelect
   * @param {object} api
   */
  function initDeliveryCalendar(root, config, dateSelect, api) {
    const calendar = root.querySelector('[data-pds-delivery-calendar]');
    if (!calendar || !useCalendarPicker(api)) return;

    if (calendar.dataset.pdsCalendarInit === 'true') return;
    calendar.dataset.pdsCalendarInit = 'true';

    root.dataset.datePickerMode = 'calendar';

    const displayInput = calendar.querySelector('[data-pds-calendar-display]');
    const toggleBtn = calendar.querySelector('[data-pds-calendar-toggle]');
    const panel = calendar.querySelector('[data-pds-calendar-panel]');
    const prevBtn = calendar.querySelector('[data-pds-calendar-prev]');
    const nextBtn = calendar.querySelector('[data-pds-calendar-next]');
    const grid = calendar.querySelector('[data-pds-calendar-grid]');

    if (!displayInput || !panel || !grid) return;

    const { earliest, max } = api.getSelectableRange(config);
    let holidays = api.getHolidaySet(config);

    let rangeEarliest = earliest;
    let rangeMax = max;
    let selectedYmd = dateSelect.value || config.selectedDate || '';
    let viewYear = rangeEarliest.getFullYear();
    let viewMonth = rangeEarliest.getMonth();

    if (selectedYmd) {
      const selected = api.parseYmd(selectedYmd);
      if (!Number.isNaN(selected.getTime())) {
        viewYear = selected.getFullYear();
        viewMonth = selected.getMonth();
      }
    }

    const syncDisplay = (ymd = selectedYmd || dateSelect.value) => {
      displayInput.value = formatYmdSlash(ymd, api);
    };

    const closePanel = () => {
      panel.hidden = true;
      if (toggleBtn) toggleBtn.setAttribute('aria-expanded', 'false');
    };

    const openPanel = () => {
      panel.hidden = false;
      if (toggleBtn) toggleBtn.setAttribute('aria-expanded', 'true');
      render();
    };

    const syncSelectValue = (ymd) => {
      if (typeof api.setDateSelectValue === 'function') {
        api.setDateSelectValue(dateSelect, ymd);
      } else {
        dateSelect.value = ymd;
      }
    };

    const onSelect = (ymd) => {
      if (!ymd) return;

      root.dataset.pdsDeliveryUserPicked = 'true';
      selectedYmd = ymd;
      syncDisplay(ymd);
      syncSelectValue(ymd);
      render();
      closePanel();
      dateSelect.dispatchEvent(new Event('change', { bubbles: true }));
    };

    const render = () => {
      const activeYmd = selectedYmd || dateSelect.value || '';
      renderMonth(calendar, {
        api,
        viewYear,
        viewMonth,
        selectedYmd: activeYmd,
        earliest: rangeEarliest,
        max: rangeMax,
        holidays,
      });
    };

    grid.addEventListener('click', (event) => {
      const target = event.target;
      if (!(target instanceof Element)) return;

      const dayButton = target.closest('button[data-date]');
      if (!dayButton || dayButton.disabled || !grid.contains(dayButton)) return;

      event.preventDefault();
      event.stopPropagation();

      const ymd = dayButton.dataset.date;
      if (!ymd) return;

      const cellDate = api.parseYmd(ymd);
      if (!isDaySelectable(cellDate, rangeEarliest, rangeMax, holidays, api)) return;

      onSelect(ymd);
    });

    syncDisplay();

    const openToggle = (event) => {
      event.preventDefault();
      if (panel.hidden) openPanel();
      else closePanel();
    };

    displayInput.addEventListener('click', openToggle);
    toggleBtn?.addEventListener('click', openToggle);

    prevBtn?.addEventListener('click', (event) => {
      event.stopPropagation();
      if (viewMonth === 0) {
        viewYear -= 1;
        viewMonth = 11;
      } else {
        viewMonth -= 1;
      }
      render();
    });

    nextBtn?.addEventListener('click', (event) => {
      event.stopPropagation();
      if (viewMonth === 11) {
        viewYear += 1;
        viewMonth = 0;
      } else {
        viewMonth += 1;
      }
      render();
    });

    panel.addEventListener('click', (event) => {
      event.stopPropagation();
    });

    const onDocumentClick = (event) => {
      if (panel.hidden) return;
      const target = event.target;
      if (!(target instanceof Node)) return;
      if (calendar.contains(target)) return;
      closePanel();
    };

    document.addEventListener('click', onDocumentClick);
    calendar.pdsCalendarDocumentClick = onDocumentClick;

    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') closePanel();
    });

    dateSelect.addEventListener('change', () => {
      selectedYmd = dateSelect.value || selectedYmd;
      syncDisplay();
      render();
    });

    root.pdsDeliveryCalendarRefresh = () => {
      const range = api.getSelectableRange(config);
      rangeEarliest = range.earliest;
      rangeMax = range.max;
      holidays = api.getHolidaySet(config);
      selectedYmd = dateSelect.value || config.selectedDate || selectedYmd;
      syncDisplay();
      render();
    };
  }

  window.PdsDeliveryCalendar = {
    init: initDeliveryCalendar,
    useCalendarPicker,
  };
})();
